import type { StudioContextReference, StudioDefinition, StudioTemplate } from 'src/components/star-grid/reportStudio.types';
import type { StarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { STUDIO_HIDDEN_CONTEXT_KEYS, STUDIO_SOURCE_MODE } from 'src/components/star-grid/reportStudioConstants';

export const unresolvedStudioContext = (value: string): boolean => value.includes('$<<');
const contextReference = (expression: string): StudioContextReference | undefined => {
  const match = /^\$<<[^.<>]+\.(\}apq_[^.<>]+)\.\1>>$/i.exec(expression.trim());
  return match ? { expression, parameter_key: match[1].slice(1).toLowerCase().replace(/\s+/g, '_') } : undefined;
};
const contextValue = (definition: StudioDefinition, reference: StudioContextReference): string => {
  const values = definition.parameters[reference.parameter_key] ?? [];
  return values.length === 1 && values[0].trim() && !unresolvedStudioContext(values[0]) ? values[0] : reference.expression;
};
export const resolveStudioContext = (
  definition: StudioDefinition,
  references: Readonly<Record<string, StudioContextReference>> = {}
): StudioDefinition => ({
  ...definition,
  fixed_context: {
    ...definition.fixed_context,
    ...Object.fromEntries(Object.entries(references).map(([key, reference]) => [key, contextValue(definition, reference)]))
  }
});
export const prepareStudioTemplate = (template: StudioTemplate): StudioTemplate => {
  const references = Object.fromEntries(
    Object.entries(template.definition.fixed_context).flatMap(([key, value]) => {
      const reference = contextReference(value);
      return reference ? [[key, reference]] : [];
    })
  );
  return {
    ...template,
    context_references: references,
    definition: resolveStudioContext({ ...template.definition, source_mode: STUDIO_SOURCE_MODE }, references)
  };
};
export const visibleStudioContext = (definition: StudioDefinition, text: StarGridText) =>
  Object.entries(definition.fixed_context)
    .filter(([key]) => !STUDIO_HIDDEN_CONTEXT_KEYS.includes(key))
    .map(([key, value]) => ({
      key,
      value: unresolvedStudioContext(value) ? text('studioContextUnresolved') : value
    }));
